import React from 'react';
import { 
  BuildingStorefrontIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ArrowTrendingDownIcon,
  ArrowTrendingUpIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { useFarm } from '../contexts/FarmContext';
import { useAuth } from '../contexts/AuthContext';
import { useDashboardData } from '../hooks/useSupabaseData';
import { useAdminDashboard } from '../hooks/useAdminDashboard';
import { useIotSummary } from '../hooks/useIotSummary';
import FarmSelector from '../components/FarmSelector';
import OrganizationSelector from '../components/OrganizationSelector';
import FarmMapOverview from '../components/FarmMapOverview';
import { dashboardStats } from '../data/mockData';

const StatCard = ({ title, value, subtitle, color = 'primary', icon: Icon, trend }) => {
  const colorClasses = {
    primary: 'bg-blue-50 text-blue-600 border-blue-200',
    success: 'bg-green-50 text-green-600 border-green-200',
    danger: 'bg-red-50 text-red-600 border-red-200',
    warning: 'bg-yellow-50 text-yellow-600 border-yellow-200'
  };

  return (
    <div className={`card border-l-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          {trend && (
            <div className={`flex items-center mt-1 text-sm ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {trend.isPositive ? '↗' : '↘'} {trend.percentage}
            </div>
          )}
        </div>
        {Icon && (
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        )}
      </div>
    </div>
  );
};

const ProduceChart = ({ data, selectedYear }) => {
  const maxWeight = Math.max(...data.map(item => item.weight));
  const yearText = selectedYear ? selectedYear : 'All Years';
  
  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Produce For {yearText} (estimated harvest)</h3>
      <div className="space-y-4">
        {data.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
            </div>
            <h4 className="text-sm font-medium text-gray-900 mb-1">No Harvest Data Available</h4>
            <p className="text-sm text-gray-500">
              No estimated harvest data found for the selected period.
            </p>
          </div>
        ) : (
          data.map((item, index) => {
          const percentage = (item.weight / maxWeight) * 100;
          return (
            <div key={index} className="flex items-center space-x-4">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-sm font-medium text-primary-600">
                  {item.name.charAt(0)}
                </span>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900">{item.name}</span>
                  <span className="text-sm text-gray-500">{item.weight.toLocaleString()} kg</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full transition-all duration-500"
                    style={{ 
                      width: `${percentage}%`,
                      backgroundColor: item.color 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })
        )}
      </div>
    </div>
  );
};

// One row of the "IoT Device Status" box: how many devices of a kind are
// registered. `device` is { status, count } from useIotSummary and may be
// missing. The dot is the same neutral colour on every row: nothing reports
// whether a device is online.
const IotDeviceCount = ({ label, device }) => {
  const status = device?.status;
  const hasCount = status === 'ok' && Number.isFinite(device?.count);
  // A count that could not be loaded is "not available", never 0
  const failed = status === 'error' || (status === 'ok' && !hasCount);

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      {hasCount ? (
        <span className="font-medium">{device.count}</span>
      ) : failed ? (
        <span className="text-sm text-gray-500">not available</span>
      ) : (
        <span className="text-sm text-gray-400">Loading…</span>
      )}
    </div>
  );
};

const Dashboard = () => {
  // State for year filter (null = all years)
  const [selectedYear, setSelectedYear] = React.useState(null);
  
  // Get farm context and data
  const { 
    getActiveFarmIds, 
    selectedFarmId, 
    setSelectedFarmId,
    farms,
    isAdmin, 
    loading: farmLoading, 
    hasFarms,
    organizations,
    selectedOrgId,
    setSelectedOrgId,
    hasOrganizations
  } = useFarm();
  
  // Check if viewing all farms as admin (only when no org filter is applied)
  const isViewingAllFarms = isAdmin && selectedFarmId === 'all' && selectedOrgId === 'all';
  
  // Get admin stats - always call hooks unconditionally (React rule)
  const adminDashboard = useAdminDashboard();
  const adminStats = adminDashboard?.stats || null;
  const adminLoading = adminDashboard?.loading || false;
  
  // Get regular dashboard data for specific farms
  const { data: stats, loading: dataLoading, error, availableYears } = useDashboardData(getActiveFarmIds(), selectedYear);

  // IoT boxes: registered device counts and the latest plant photo analyses,
  // for the selected farm (or every farm). Each part may still be loading or
  // unavailable; the boxes below cope with a missing value.
  const { user } = useAuth();
  const iotSummary = useIotSummary(user?.id, selectedFarmId);
  const iotDevices = iotSummary?.devices || {};
  const iotActivity = iotSummary?.activity || {};
  const iotActivityItems = Array.isArray(iotActivity.items) ? iotActivity.items : [];
  
  // Use admin stats when viewing all farms AND no year filter applied
  // When year filter is applied, use regular stats which support year filtering
  let farm, sales, cost, metrics, production, activities;
  
  if (isViewingAllFarms && adminStats && !selectedYear) {
    // Transform admin stats to dashboard format with FULL data
    farm = {
      count: adminStats.total_farms || 0,
      totalFarms: adminStats.total_farms || 0,
      totalSize: adminStats.total_size || 0,
      avgSize: adminStats.avg_size || 0,
      season: adminStats.active_seasons || 0
    };
    sales = {
      total: adminStats.total_revenue || 0,
      totalRevenue: adminStats.total_revenue || 0,
      avgOrderSize: adminStats.avg_sale_value || 0,
      avgWeight: adminStats.avg_weight_per_sale || 0,
      avgPerFarm: adminStats.avg_revenue_per_farm || 0
    };
    cost = {
      total: adminStats.total_expenses || 0,
      netProfit: adminStats.net_profit || 0,
      profitMargin: adminStats.total_revenue > 0 
        ? ((adminStats.net_profit / adminStats.total_revenue) * 100).toFixed(1)
        : 0,
      avgPerFarm: adminStats.avg_cost_per_farm || 0,
      avgProfitPerFarm: adminStats.avg_profit_per_farm || 0
    };
    metrics = {
      totalSeasons: adminStats.total_seasons || 0,
      activeSeasons: adminStats.active_seasons || 0,
      completedSeasons: adminStats.completed_seasons || 0,
      totalExpectedHarvest: adminStats.total_expected_harvest || 0,
      avgMarginPerKg: adminStats.total_weight_sold > 0 
        ? (adminStats.net_profit / adminStats.total_weight_sold).toFixed(2)
        : 0,
      avgOrdersPerFarm: adminStats.avg_sales_per_farm || 0
    };
    // Transform top products for chart
    production = adminStats.top_products?.map(p => ({
      produce: p.produce_name,
      estimatedHarvest: p.total_weight
    })) || [];
    activities = [];
  } else {
    // Regular dashboard data
    farm = stats?.farm || dashboardStats.farm;
    sales = stats?.sales || dashboardStats.sales;
    cost = stats?.cost || dashboardStats.cost;
    metrics = stats?.metrics || { avgMarginPerKg: 0, avgOrdersPerFarm: 0 };
    production = stats?.production || dashboardStats.production;
    activities = stats?.activities || [];
  }

  const loading = farmLoading || dataLoading || (isViewingAllFarms && adminLoading);

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex items-center flex-wrap gap-2 sm:gap-3 lg:gap-4">
          {/* Organization Selector - Admin Only */}
          {hasOrganizations && <OrganizationSelector />}

          {/* Farm Selector */}
          {hasFarms && <FarmSelector />}

          {/* Year Selector - Dynamic */}
          <select
            className="input w-40"
            value={selectedYear || 'all'}
            onChange={(e) => setSelectedYear(e.target.value === 'all' ? null : parseInt(e.target.value))}
          >
            <option value="all">All Years</option>
            {availableYears && availableYears.length > 0 ? (
              availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))
            ) : (
              // Fallback if no years available
              <>
                <option value={2025}>2025</option>
                <option value={2024}>2024</option>
                <option value={2023}>2023</option>
              </>
            )}
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600">Loading dashboard data...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading data: {error.message}</p>
        </div>
      )}

      {/* Dashboard Content */}
      {!loading && (
        <>
          {/* Main Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Farm"
          value={farm.count}
          color="primary"
          icon={BuildingStorefrontIcon}
        />
        <StatCard
          title="Season/Cycle"
          value={farm.season}
          color="primary"
          icon={CalendarIcon}
        />
        <StatCard
          title="Sales"
          value={`RM${sales.total.toLocaleString()}`}
          color="success"
          icon={CurrencyDollarIcon}
        />
        <StatCard
          title="Cost"
          value={`RM${cost.total.toLocaleString()}`}
          color="danger"
          icon={BanknotesIcon}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Size, Acre"
          value={Number(farm.totalSize).toLocaleString()}
          subtitle={`/ Avg. ${Number(farm.avgSize).toLocaleString()} acres per farm`}
          color="primary"
        />
        <StatCard
          title="Average Order Size"
          value={`RM${Number(sales.avgOrderSize).toLocaleString()}`}
          subtitle={`/ Avg. ${Number(sales.avgWeight).toLocaleString()} kg per order`}
          color="success"
        />
        <StatCard
          title="Net Profit"
          value={`RM${cost.netProfit.toLocaleString()}`}
          subtitle={`${cost.profitMargin}%`}
          color={cost.netProfit >= 0 ? "success" : "danger"}
          icon={cost.netProfit >= 0 ? ArrowTrendingUpIcon : ArrowTrendingDownIcon}
        />
      </div>

      {/* Farm Map Overview */}
      <FarmMapOverview 
        farms={farms} 
        selectedFarmId={selectedFarmId}
        onFarmSelect={(farmId) => setSelectedFarmId(farmId)}
      />

      {/* Charts and Additional Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProduceChart data={production} selectedYear={selectedYear} />
          
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Farm Performance Summary</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Sales Per Farm</span>
              <span className="font-medium">RM{sales.avgPerFarm ? Number(sales.avgPerFarm).toLocaleString() : '0'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Cost Per Farm</span>
              <span className="font-medium">RM{cost.avgPerFarm ? Number(cost.avgPerFarm).toLocaleString() : '0'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Net Profit Per Farm</span>
              <span className={`font-medium ${cost.avgProfitPerFarm >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                RM{cost.avgProfitPerFarm ? Number(cost.avgProfitPerFarm).toLocaleString() : '0'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Avg. Margin Per kg</span>
              <span className={`font-medium ${metrics.avgMarginPerKg >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {metrics.avgMarginPerKg >= 0 ? '' : 'RM'}{metrics.avgMarginPerKg}
              </span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-gray-600">Avg. Order Per Farm</span>
              <span className="font-medium">{metrics.avgOrdersPerFarm || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* IoT Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">IoT Device Status</h3>
          <div className="space-y-3">
            <IotDeviceCount label="Avialite LEDs registered" device={iotDevices.leds} />
            <IotDeviceCount label="HAVVA cameras registered" device={iotDevices.cameras} />
            <IotDeviceCount label="Elliance sensors registered" device={iotDevices.sensors} />
          </div>
          <p className="mt-4 text-xs text-gray-500">
            Devices registered under your account. Online status is not reported yet.
          </p>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activities</h3>
          <div className="space-y-3">
            {iotActivity.status === 'error' ? (
              <p className="text-sm text-gray-500">Recent activity is not available.</p>
            ) : iotActivity.status !== 'ok' ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : iotActivityItems.length === 0 ? (
              <p className="text-sm text-gray-500">No recent IoT activity.</p>
            ) : (
              iotActivityItems.map((item) => (
                <div key={item.id} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-sm text-gray-900">{item.title}</p>
                    {item.detail && <p className="text-xs text-gray-500">{item.detail}</p>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
